import { makeStyles, HtmlContent, getPresentationContentStyles, useMediaQuery } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { FormSection, useClientOptions, useUser } from '@paintscout/ui-business'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import { getDetails, getQuoteOptions, setQuoteOptions, getQuoteFeatureProducts } from '@paintscout/util/builder'
import { isEmpty, isQuoteEditable } from '@paintscout/util/util'
import classnames from 'classnames'
import moment from 'moment'
import type { QuoteDocument } from 'paintscout'
import React, { useMemo, useState } from 'react'
import { track } from 'shared/util/analytics'
import type { Item } from '../ItemTable'
import TotalTableNext from './TotalTableNext'
import { useQuote } from '../context'
import BulkActions from '../Quote/BulkActions'
import MediaSectionNext from './MediaSectionNext'
import InvoiceAlert from '../Quote/InvoiceAlert'
import QuoteTerms from '../Quote/QuoteTerms'
import PaymentsTableNext from './PaymentsTableNext'
import ProductsTableNext from './ProductsTableNext'
import AdditionalTableNext from './AdditionalTableNext'
import BidTableNext from './BidTableNext'
import OptionsAddedTableNext from './OptionsAddedTableNext'
import OptionsTableNext from './OptionsTableNext'
import PendingTableNext from './PendingTableNext'
import QuoteDocumentHeader from './QuoteDocumentHeader/QuoteDocumentHeader'
import { useWisetack } from '../Wisetack'
import { WisetackDisclaimerContent } from '../Wisetack/WisetackPromoDisclaimer'

const ADDED_OPTIONS_SECTION = 'added-options' as const

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      // Explicit body color, because nothing above supplies one any more. The legacy document sat
      // inside a MUI `<Paper>`, which sets `color: palette.text.primary`; the redesign renders a
      // plain div instead (Quote.tsx `nextWrapper`), so every `color: inherit` node below — and
      // getPresentationContentStyles sets exactly that on h1-h4/p/ul/ol/a — inherited from whatever
      // ancestor happened to set a color. Inside a v2 presentation that ancestor is the
      // Presentation root, which spreads the presentation theme's `typography.body1` (accent color
      // when the style maps body to the accent), so the whole estimate rendered in the account's
      // accent colour in customer-view, the preview dialog and the PDF.
      // P40 is `blackText` in createTheme, i.e. the `palette.text.primary` value Paper used to hand
      // down — hardcoded rather than read off the theme because inside a presentation
      // `palette.text.primary` is the presentation's own configurable text colour.
      color: theme.paintscout.palette.brand.Neutral.P40,
      ...getPresentationContentStyles(theme, { noMargins: ['li'] })
    },
    // One shared card holding the document header + the main item section, so they read as a single
    // sheet (the header is rendered `flush` at the top, then a divider, then the item table). Other
    // sections (Payments/Options/Products) remain their own cards below, matching the mock.
    documentCard: {
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
      borderRadius: theme.borderRadius.lg,
      backgroundColor: theme.paintscout.palette.brand.Neutral.White,
      overflow: 'hidden',
      '@media print': {
        border: 'none'
      }
    },
    documentBody: {
      padding: theme.spacing(2),
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`
    },
    // The customer sees no shared card on a phone: the header is its own card and each item shell
    // stands alone, so the shells aren't outlined twice (ENG-2075, matches the prototype).
    documentCardOpen: {
      border: 'none',
      borderRadius: 0,
      backgroundColor: 'transparent',
      overflow: 'visible'
    },
    documentBodyOpen: {
      padding: 0,
      borderTop: 'none'
    },
    // Tuck the Total summary card directly under the item table with a small, deliberate gap
    // (matches the mock). Explicit so the spacing doesn't drift with the surrounding flow.
    totalRow: {
      marginTop: theme.spacing(1)
    },
    disclaimerWrapper: {
      marginTop: theme.spacing(2),
      textAlign: 'center'
    },
    spacer: {},
    hidePrint: {
      '@media print': { display: 'none' }
    },
    noteSection: {
      background: theme.palette.grey['100'],
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2)
    },
    optionsTable: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
      marginCollapse: 'separate'
    },
    paymentsTable: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2),
      marginCollapse: 'separate'
    }
  }),
  { name: 'QuoteNextView' }
)

const BASE_SELECTED = {
  bid: [],
  options: [],
  additional: [],
  pending: [],
  archived: [],
  [ADDED_OPTIONS_SECTION]: []
}

interface SelectedItems {
  bid: RenderableItem[]
  options: RenderableItem[]
  additional: RenderableItem[]
  pending: RenderableItem[]
  archived: RenderableItem[]
  'added-options': RenderableItem[]
}

/**
 * The estimate document body for the redesign (estimateNext) — a fresh fork of QuoteView that
 * renders the redesigned `QuoteDocumentHeader` and reuses the existing item tables. It now serves
 * both contexts: the editor, and the customer-facing presentation (customer-view renders it through
 * `Quote` once the server-evaluated `estimateNext` flag is on). The presentation/customer-view
 * branches below mirror the legacy `QuoteView` one-for-one — they're the difference between a
 * customer seeing pending payments, duplicated Media/Terms sections and no tier selector, and not.
 */
export interface QuoteNextViewProps {
  /**
   * Whether the document renders inside a presentation. Unset falls back to the quote context's
   * `isPresentation`. See `inPresentation` on `QuoteProps`.
   */
  inPresentation?: boolean
  /** See `showMediaSection` on `QuoteProps`. Unset keeps the `!inPresentation` default. */
  showMediaSection?: boolean
}

function QuoteNextView(props: QuoteNextViewProps) {
  const classes = useStyles()
  const [selectedItems, setSelectedItems] = useState<SelectedItems>(BASE_SELECTED)
  const { options } = useClientOptions()
  const { allowQuoteAdditionalWork } = getQuoteOptions({ options })
  const {
    tableView,
    quote,
    isEditable,
    canAddAdditionalWork,
    isPresentation,
    isCustomerView,
    wisetackPromo,
    onEditNote,
    onItemAction,
    updateQuote,
    setSelectedItemKeys
  } = useQuote()
  const { preferences, user } = useUser()
  /**
   * The prop wins over the context. `Quote` passes it down from its own `inPresentation` prop, the
   * way the legacy `QuoteView` has always taken it (QuoteView.tsx `props.inPresentation`). No v2
   * presentation surface sets `isPresentation` on QuoteContextProvider, so context alone left this
   * false inside a v2 presentation — and the `!inPresentation` Terms/Media branches below then
   * rendered a second copy next to the presentation's own `terms`/`pictures` block (#22699).
   */
  const inPresentation = props.inPresentation ?? !!isPresentation
  /** Unset means the caller has no opinion — see `showMediaSection` on `QuoteProps` (#22698). */
  const showMediaSection = props.showMediaSection ?? !inPresentation
  // The customer view and the preview dialog both render the document as the customer sees it.
  const customerFacing = inPresentation || !!isCustomerView
  const isMobile = useMediaQuery('xs')
  const openDocument = customerFacing && isMobile
  const hasOptionsApproval = options?.options?.optionsApproval?.enabled
  const details = useMemo(() => getDetails({ quote, options }), [quote, options])
  const hasClientNote = !isEmpty(details.notes.client)
  const { showQuoteFiles, showQuoteOptions, showQuoteProducts, showQuotePayments } = getQuoteOptions({ quote, options })
  const allowQuotePayments = options.options.quotes?.allowQuotePayments

  const showPaymentsTable =
    (!!quote.totals.after_tax && (quote.is_invoice || allowQuotePayments)) || (quote.payments ?? []).length > 0
  // In the customer-facing document, pending payments aren't the customer's business — only settled
  // ones are listed.
  const showPaymentsTablePresentation =
    !!quote.totals.after_tax && (quote.payments ?? []).filter((payment) => payment.status !== 'pending').length > 0
  // The classic editor is read-only for any non-draft or archived quote until you click Edit, so its
  // `!isEditable` terms gate shows the terms (and signature) on every sent quote. The next editor
  // has no edit mode and stays editable, so the terms gate checks the quote itself (ENG-2104).
  const showTerms = !inPresentation && (!isEditable || !isQuoteEditable(quote))
  const featureProducts = getQuoteFeatureProducts({ quote, options })
  const hasAdditionalWork = quote.is_invoice || allowQuoteAdditionalWork

  const { showOnOldDocuments } = useWisetack({ user, options, preferences })
  const wisetackSignup = options.options.wisetackSignup

  // The disclosure must appear wherever the promo does (the promo carries the "*"). Mirrors
  // WisetackPromo's hideBasedOnSignup — see legacy QuoteView for the history (ENG-833).
  const showWisetackDisclaimer =
    !!wisetackPromo &&
    !wisetackPromo.error &&
    !(inPresentation && (!quote.contact || quote.contact?.company)) &&
    !(
      wisetackSignup?.date &&
      quote?.dates?.created &&
      moment(wisetackSignup.date).isAfter(quote.dates.created) &&
      !showOnOldDocuments
    )

  const handleBulkActionClick = (action: string, previousSection: QuoteItemSection) => {
    const keepSelectionActions = ['swap-products']
    return (selectedItems: RenderableItem[]) => {
      track('quote_items_bulk_action', { action, section: previousSection, count: selectedItems.length })
      if (action === 'swap-products') {
        track('product_swap_opened', { source: 'bulk_actions', count: selectedItems.length })
      }
      onItemAction(
        action,
        previousSection,
        selectedItems.map((item) => item.key)
      )
      if (!keepSelectionActions.includes(action)) {
        resetSelectedItems()
      }
    }
  }

  const handleItemSelection = ({
    section,
    selectedItems: selectedItemsOuter
  }: {
    section: QuoteItemSection
    selectedItems: Item[]
  }) => {
    setSelectedItems((prev) => {
      const next = { ...prev, [section]: selectedItemsOuter }
      const allKeys = [
        ...next.bid,
        ...next.options,
        ...next.additional,
        ...next.pending,
        ...next.archived,
        ...(next['added-options'] || [])
      ].map((item) => item.key)
      setSelectedItemKeys?.(allKeys)
      return next
    })
  }

  return (
    <>
      <div className={classes.root} id={'quote'} data-testid="quote-next">
        <InvoiceAlert inPresentation={inPresentation} />
        <div className={classnames(classes.documentCard, { [classes.documentCardOpen]: openDocument })}>
          <QuoteDocumentHeader flush customerFacing={customerFacing} />

          <div className={classnames(classes.documentBody, { [classes.documentBodyOpen]: openDocument })}>
            <BidTableNext
              selectedItems={getSelectedItems({ section: 'bid' })}
              onItemSelection={(_event, selectedItems) => handleItemSelection({ section: 'bid', selectedItems })}
              onDeselectItem={handleDeselectItem}
              onItemAction={(action, section, item) => handleItemAction(action, section, item, onItemAction)}
            />
            {hasOptionsApproval && (
              <OptionsAddedTableNext
                selectedItems={getSelectedItems({ section: ADDED_OPTIONS_SECTION })}
                onItemAction={(action, section, item) => handleItemAction(action, section, item, onItemAction)}
                onItemSelection={(_event, selectedItems) =>
                  handleItemSelection({ section: ADDED_OPTIONS_SECTION, selectedItems })
                }
              />
            )}
            {hasAdditionalWork && (
              <AdditionalTableNext
                selectedItems={getSelectedItems({ section: 'additional' })}
                onItemSelection={(_event, selectedItems) =>
                  handleItemSelection({ section: 'additional', selectedItems })
                }
                onItemAction={(action, section, item) => handleItemAction(action, section, item, onItemAction)}
              />
            )}
            <PendingTableNext
              selectedItems={getSelectedItems({ section: 'pending' })}
              onItemSelection={(_event, selectedItems) => handleItemSelection({ section: 'pending', selectedItems })}
              onItemAction={(action, section, item) => handleItemAction(action, section, item, onItemAction)}
            />

            {details.totals.show && (
              <div className={classes.totalRow}>
                <TotalTableNext />
              </div>
            )}

            {hasClientNote && (
              <div className={classnames(classes.spacer, classes.noteSection)} onClick={isEditable ? onEditNote : null}>
                <HtmlContent content={details.notes.client} />
              </div>
            )}

            {(inPresentation ? showPaymentsTablePresentation : showPaymentsTable) && (
              <div
                className={classnames({
                  [classes.spacer]: true,
                  [classes.paymentsTable]: true,
                  [classes.hidePrint]: !showQuotePayments,
                  'quote-view-payments-table': true
                })}
              >
                <PaymentsTableNext
                  inPresentation={inPresentation}
                  onChange={handlePaymentsChange}
                  collapsed={!showQuotePayments}
                  onToggle={(ev: any) => handleToggle(ev, 'showQuotePayments')}
                />
              </div>
            )}

            <div className={classnames(classes.spacer, classes.optionsTable)}>
              <OptionsTableNext
                showAddButton={isEditable || canAddAdditionalWork}
                selectedItems={getSelectedItems({ section: 'options' })}
                onItemSelection={(_event, selectedItems) => handleItemSelection({ section: 'options', selectedItems })}
                onItemAction={(action, section, item) => handleItemAction(action, section, item, onItemAction)}
                collapsed={!showQuoteOptions}
                onToggle={(ev: any) => handleToggle(ev, 'showQuoteOptions')}
                inPresentation={inPresentation}
              />
            </div>

            {/* Nothing to list means no section at all — the Products card carries no add affordance of
                its own, so an empty one is pure noise on a new estimate (ENG-1478). A template is the
                exception: it never lists products, and the section says why (ENG-1282). */}
            {(featureProducts.length > 0 || quote?.type === 'quoteTemplate') && (
              <div className={classnames(classes.spacer, classes.optionsTable)}>
                <ProductsTableNext
                  collapsed={!showQuoteProducts}
                  onToggle={(ev: any) => handleToggle(ev, 'showQuoteProducts')}
                  inPresentation={inPresentation}
                />
              </div>
            )}

            {showMediaSection && (
              <div className={classnames(classes.spacer, { [classes.hidePrint]: !showQuoteFiles })}>
                <MediaSectionNext
                  variant="quote"
                  collapsed={!showQuoteFiles}
                  onToggle={(ev: any) => handleToggle(ev, 'showQuoteFiles')}
                />
              </div>
            )}

            {/* Likewise Terms — the presentation renders them as their own section. */}
            {showTerms && (
              <FormSection hideDivider={true}>
                <QuoteTerms isQuoteView={true} />
              </FormSection>
            )}
            {showWisetackDisclaimer && (
              <div className={classes.disclaimerWrapper}>
                <WisetackDisclaimerContent wisetackPromo={wisetackPromo} />
              </div>
            )}
          </div>
        </div>
      </div>
      {(isEditable || canAddAdditionalWork) && (
        <BulkActions
          options={options}
          quote={quote}
          tableView={tableView}
          getSelectedItems={getSelectedItems}
          getSelectedItemsByType={getSelectedItemsByType}
          onBulkActionClick={(action, previousSection) =>
            handleBulkActionClick(action, previousSection as QuoteItemSection)
          }
          onDeselectAll={handleDeselectAll}
          alwaysShowSwap
        />
      )}
    </>
  )

  function getSelectedItems(args?: { section?: QuoteItemSection }): RenderableItem[] {
    const { section } = args ?? {}
    const { bid, options, additional, pending, archived } = selectedItems
    if (section) {
      return selectedItems[section]
    }
    return [...bid, ...options, ...additional, ...pending, ...archived, ...selectedItems[ADDED_OPTIONS_SECTION]]
  }

  function getSelectedItemsByType(type: string): RenderableItem[] {
    const { bid, options, additional, pending, archived } = selectedItems
    const items = [...bid, ...options, ...additional, ...pending, ...archived]
    return items.filter((item) => item.type === type)
  }

  function handleItemAction(action: string, section: QuoteItemSection, item: RenderableItem, onItemAction: any) {
    const moveActions = ['make-option', 'archive', 'unarchive', 'move-to-bid', 'move-to-additional', 'move-to-pending']
    if (moveActions.includes(action)) {
      handleDeselectItem({ section, key: item.key })
    }
    onItemAction?.(action, section, [item.key])
  }

  function handleDeselectItem({ section, key }: { section: QuoteItemSection | 'all'; key?: string }) {
    if (section === 'all') {
      handleDeselectAll()
    } else {
      setSelectedItems((selectedItems) => ({
        ...selectedItems,
        [section]: selectedItems[section].filter((items) => items.key !== key)
      }))
    }
  }

  function handleDeselectAll() {
    resetSelectedItems()
  }

  function handlePaymentsChange(quote: QuoteDocument) {
    updateQuote({ quote })
  }

  function handleToggle(event: any, name: string) {
    const quoteOptions = getQuoteOptions({ options, quote })
    quoteOptions[name] = !quoteOptions[name]
    const updatedQuote = setQuoteOptions({ quoteOptions, quote })
    updateQuote({ quote: updatedQuote })
  }

  function resetSelectedItems() {
    setSelectedItems(BASE_SELECTED)
    setSelectedItemKeys?.([])
  }
}

export default QuoteNextView
